import type * as THREE from 'three';
import type { ModelAnimation } from '@vrlearning/lesson-schema';
import { LOOK_STEP, pressStep } from '../engine/test-panel.js';
import { BekijkenPanel } from '../render/bekijken-panel.js';
import type { Stage } from '../render/stage.js';
import { TeleportMarker } from '../render/teleport-marker.js';
import type { ExtraPressable } from '../render/ui.js';
import type { BekijkenAction } from './panel.js';
import type { BekijkenPlan } from './plan.js';

/** What Bekijken mode needs of the running lesson (main.ts's `current`). */
export type BekijkenTarget = {
  readonly ui: {
    readonly group: THREE.Group;
    setExtraButtons(buttons: readonly ExtraPressable[]): void;
  };
  readonly player: {
    readonly state: { readonly step: { readonly id: string } };
    goto(stepId: string): void;
  };
  setWorldStates(states: ReadonlyMap<string, string>): void;
};

/**
 * Hangs Bekijken mode on a lesson the player runs with a bare lectern (ADR-058): the panel in the
 * lectern's group, the teleport points on the floor, both pressed like the lectern's buttons. A
 * feature's state is set on the world at once (animated, as a step would); a clip is its step, played
 * looped with its sounds (pressed again: from the start); a point is a fade teleport. Every press is
 * `onInput` (the idle timer). Returns how to take it all away again.
 */
export function hangBekijken(input: {
  target: BekijkenTarget;
  stage: Pick<Stage, 'scene' | 'travelTo' | 'placeRig'>;
  plan: BekijkenPlan;
  title: string;
  onClose: () => void;
  onInput: () => void;
}): () => void {
  const { target, stage, plan } = input;
  const states = new Map(plan.world?.initial ?? []);
  let playing: ModelAnimation | null = null;
  let page = 0;
  let panel: BekijkenPanel | undefined;

  const markers = plan.points.map(
    (point) =>
      new TeleportMarker(point.at, () => {
        input.onInput();
        stage.travelTo(() => stage.placeRig(point.view));
      }),
  );
  for (const marker of markers) stage.scene.add(marker.mesh);

  const press = (action: BekijkenAction): void => {
    input.onInput();
    switch (action.kind) {
      case 'state':
        states.set(action.feature, action.state);
        target.setWorldStates(new Map(states));
        break;
      case 'clip':
        playing = action.clip;
        pressStep(target.player, action.clip === null ? LOOK_STEP : `anim-${action.clip}`);
        break;
      case 'page':
        page = action.page;
        break;
      case 'close':
        input.onClose();
        return;
      case 'none':
        return;
    }
    hang();
  };

  const hang = (): void => {
    panel?.dispose();
    panel = new BekijkenPanel(
      {
        title: input.title,
        features: plan.world?.features ?? {},
        states,
        clips: plan.object?.clips ?? [],
        playing,
        page,
      },
      press,
    );
    target.ui.group.add(panel.group);
    target.ui.setExtraButtons([...panel.pressable, ...markers]);
  };
  hang();

  return () => {
    target.ui.setExtraButtons([]);
    panel?.dispose();
    panel = undefined;
    for (const marker of markers) marker.dispose();
  };
}
